import type { Score } from '@/interface/ILiveScore';
import type { IBetBuilderResult, IDynamicMarketResult, IDynamicMarketWonResult, IMatchResult, IResult } from '@/interface/IMatchResult';
import type { ResultType } from '@/interface/enum';
import { FGLGResult, ScoringType, SelectionOutcome, SportType } from '@/interface/enum';
import type { DynamicMarketResultBySelectionResponse, DynamicMarketResultResponse, MarketResultResponse, MatchResponse, MatchResultResponse } from '@/interface/pollux';
import { parseISOString } from '@/libraries/utils/date';

const formatMatchResult = (matchResult: MatchResultResponse, sportType: SportType): IMatchResult => ({
    sportType,
    matchResultId: matchResult.matchResultId,
    betTypeGroupId: matchResult.betTypeGroupId,
    betTypeGroupName: matchResult.betTypeGroupName ?? '',
    status: matchResult.status,
    thirdPartyCode: matchResult.thirdPartyCode,
    homeOption: matchResult.homeOption ?? '',
    awayOption: matchResult.awayOption ?? '',
    // because cricket scoringType in database always be wrong
    scoringType: sportType === SportType.Cricket ? ScoringType.Cricket : ScoringType.fromPollux(matchResult.scoringType),
    gameScores: matchResult.gameScores as Record<string, Score>,
    totalScores: matchResult.scores as Partial<Record<ResultType, Score>>,
});

export const formatResults = (sportType: SportType, matches: MatchResponse[]): IResult[] => matches.map<IResult>((match: MatchResponse) => ({
    sportType,
    leagueId: match.leagueId,
    leagueName: match.leagueName,
    matchId: match.matchId,
    matchCode: match.matchCode,
    leaguePriority: match.leaguePriority,
    matchCodeWithoutLeaguePriority: match.matchCodeWithoutLeaguePriority,
    showTime: parseISOString(match.showTime),
    showTimeDisplayType: match.showTimeDisplayType,
    homeTeam: match.homeTeamName,
    awayTeam: match.awayTeamName,
    matchResults: match.results.map(result => formatMatchResult(result, sportType)),
    firstGoal: FGLGResult.fromPollux(match.firstGoal),
    lastGoal: FGLGResult.fromPollux(match.lastGoal),
    hasBetBuilderResults: match.hasBetBuilderResults,
    hasDynamicMarketResults: match.hasDynamicMarketResults,
}));

export function formatBetBuilderResults(results: MarketResultResponse[]): IBetBuilderResult[] {
    return results.map(result => ({
        point: result.point ?? null,
        marketType: {
            id: result.marketType.id,
            name: result.marketType.name,
        },
        isRefunded: result.isRefunded,
        selectionResults: result.selectionResults?.map(x => ({
            outcome: SelectionOutcome.fromPollux(x.outcome),
            name: x.name,
        })) ?? [],
    }));
}

export function formatDynamicMarketResults(results: DynamicMarketResultResponse[]): IDynamicMarketResult[] {
    return results.map(result => ({
        marketId: result.matchMarketId,
        marketName: result.marketName,
        isRefunded: result.isRefunded,
        selectionResults: result.selectionResults?.map(x => ({
            outcome: SelectionOutcome.fromPollux(x.outcome),
            name: x.selectionName,
            selectionId: x.selectionId,
        })) ?? [],
    }));
}

export function formatDynamicMarketResultBySelection(result: DynamicMarketResultBySelectionResponse): IDynamicMarketWonResult {
    return {
        wonSelectionName: result.winnerSelectionName ?? null,
    };
}
